import { Box } from '@mui/material';
import {
  DataGridPremium,
  GridCallbackDetails,
  GridColDef,
  GridRowId,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { BaseCampaignItem } from '@retail/campaign/types';
import React, { useCallback, useMemo } from 'react';
import { getDataGridLocalization, MgPrisLocale } from '@retail/utils';
import { useRowColorGrouping } from '@retail/hooks';
import { AddButton, NoTableRowsOverlay } from '@retail/components';
import { useCampaignTFunction } from '@retail/campaign/i18n';

interface Props<T extends BaseCampaignItem> {
  items: T[];
  language: string;
  columns: GridColDef<T>[];
  checkboxSelection?: {
    checkedItemIds: string[];
    checkItemsHandler: (ids: string[]) => void;
  };
  status: 'success' | 'error' | 'loading';
  refetchItems: () => void;
}

export const CampaignItemsTable = <T extends BaseCampaignItem>({
  language,
  items,
  columns,
  checkboxSelection,
  status,
  refetchItems,
}: Props<T>) => {
  const t = useCampaignTFunction();
  const apiRef = useGridApiRef();
  const { getRowColorByIdGroups } = useRowColorGrouping();

  const onRowSelectionModelChange = useCallback(
    (selectionModel: GridRowSelectionModel, details: GridCallbackDetails) => {
      if (!checkboxSelection) return;
      if (details.reason === 'singleRowSelection') {
        checkboxSelection.checkItemsHandler(Array.from(selectionModel.ids) as string[]);
      } else if (checkboxSelection.checkedItemIds.length !== items.length) {
        checkboxSelection.checkItemsHandler(items.map(({ id }) => id));
      } else {
        checkboxSelection.checkItemsHandler([]);
      }
    },
    [checkboxSelection, items]
  );

  const selectionModel: GridRowSelectionModel | undefined = useMemo(
    () =>
      checkboxSelection
        ? {
            type: 'include',
            ids: new Set<GridRowId>(checkboxSelection.checkedItemIds.map((id) => id as GridRowId)),
          }
        : undefined,
    [checkboxSelection]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium
          editMode="cell"
          apiRef={apiRef}
          getRowId={(row) => row.id}
          getRowClassName={(value) =>
            getRowColorByIdGroups(
              items.map((item) => item.mgItemNumber),
              value.row.mgItemNumber,
              value.indexRelativeToCurrentPage
            )
          }
          rows={items}
          columns={columns}
          checkboxSelection={!!checkboxSelection}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          rowHeight={65}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          hideFooter
          slots={{
            noRowsOverlay: () => {
              if (status === 'error') {
                return (
                  <NoTableRowsOverlay
                    noRowsText={t('campaign.error.campaignItems.message')}
                    addAction={
                      <AddButton onClick={refetchItems}>
                        {t('campaign.error.campaignItems.retry')}
                      </AddButton>
                    }
                  />
                );
              }
              return <NoTableRowsOverlay noRowsText={t('campaign.messages.noCampaignItems')} />;
            },
          }}
          localeText={getDataGridLocalization(language as MgPrisLocale)}
          loading={status === 'loading'}
          initialState={{
            pinnedColumns: { right: ['actions'] },
          }}
          sx={{
            '.MuiDataGrid-topContainer': {
              paddingLeft: (theme) => theme.spacing(1),
            },

            '.MuiDataGrid-row': {
              paddingLeft: (theme) => theme.spacing(1),
            },
          }}
        />
      </Box>
    </Box>
  );
};
